import React, { FC, useEffect, useState, useRef, useCallback, Fragment } from "react";
import { TaxonomyType, FeatureGeoapifyType } from "data/types";
import CardNuitResume from "components/CardNuitResume/CardNuitResume";
import ItemsSlider from 'components/ItemsSlider';
import ConvertisseurMonnaie from 'components/ConvertisseurMonnaie';
import ModalEditionPDF from 'components/ModalEditionPDF';
import ModalBooking from 'components/ModalBooking';
import ModalStatistiques from 'components/ModalStatistiques';
import ModalLieuxResume from 'components/ModalLieuxResume';
import ModalModifResume from 'components/ModalModifResume';
import ModalContenuBagage from 'components/ModalContenuBagage';
import ModalImport from 'components/ModalImport';
import ModalExport from 'components/ModalExport';
import ModalIntegration from 'components/ModalIntegration';
import ModalPartageRS from 'components/ModalPartageRS';
import ModalPlannerAI from 'components/ModalPlannerAI';
import BtnLikeVoyageIcon from "components/BtnLikeVoyageIcon/BtnLikeVoyageIcon";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import BtnOutils from "components/BtnOutils";
import AIButton from "components/AIButton";
import produce from 'immer';
import {
  useDrag,
  useDrop,
  useDragLayer,
  DndProvider,
  DropTargetMonitor,
} from "react-dnd";

import {
  HTML5Backend,
  getEmptyImage,
} from "react-dnd-html5-backend";
import { voyageService } from '_services/voyage.service';
import { aiService } from "_services/ia.service";
import { useNavigate } from "react-router-dom";
import { JourneeType, EventType, PecType } from "data/types";
import { Dialog, Transition } from "@headlessui/react";
import { Tooltip }  from "react-tooltip";
import 'react-tooltip/dist/react-tooltip.css';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTranslation } from 'react-i18next';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

interface GroupVille {
  libelle1: string;
  typeTransport?: string | null;
  lat: string | null;
  longi: string | null;
  firstDay: string;
  firstDayId: number;
  image?: string;
  images: string[];
  imageItems: ResumeImageItem[];
  nights: EventType[];
}

interface ResumeImageItem {
  url: string;
  caption: string;
}

interface GroupNuits {
  pays: string;
  nom_pays: string;
  groupVilles: GroupVille[];
}

interface Flags {
  [key: string]: string | null | "loading" | undefined;
}

export interface PlanificateurResumeProps {
  className?: string;
  itemClassName?: string;
  categories?: TaxonomyType[];
  categoryCardType?: "card3" | "card4" | "card5";
  itemPerRow?: 4 | 5;
  sliderStyle?: "style1" | "style2";
  uniqueClassName: string;
  voyageKey: string;
  emailPartage: string;
  codePartage: string;
  isModif: boolean;
  onChangeModif: () => void;
  mailKo: boolean;
  eventCategoriesPage: JourneeType[];
  id_pays: string;
  libelles_pays: string;
  nbJours: number;
  visibilite: string;
  etatVoyage: number;
  nb_voyageurs: number;
  monnaie: string;
  symbole_monnaie: string,
  numDroit: number; 
  setEventCategoriesPage: (updatedCategories :any) => void;
  username: string;
  affichageBudget: string;
  nb_likes: number;
  liked: string;
  onMajLike: () => void;
  onMajDislike: () => void;
  verrouillageModif: () => Promise<number>;
}

interface DragPreviewData {
  libelle: string;
  jour: string;
  image?: string;
  nbNuits: number;
}

interface DraggableCardProps {
  index: number;
  id_journee: number;
  onDrop: (
    dragIndex: number,
    hoverIndex: number,
    draggedIdJournee: number
  ) => void;
  children?: React.ReactNode;
  eventCategories: JourneeType[];
  groupedNights: any[];
  previewData: DragPreviewData;
}

const DraggableCard: FC<DraggableCardProps> = ({
  index,
  id_journee,
  onDrop,
  children,
  previewData,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: "CARD",
      item: {
        index,
        id_journee,
        previewData,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index, id_journee, previewData]
  );

  const [, drop] = useDrop({
    accept: "CARD",
    drop: (item: any) => {
      const dragIndex = item.index;
      const hoverIndex = index;
      const draggedIdJournee = item.id_journee;

      onDrop(dragIndex, hoverIndex, draggedIdJournee);
    },
  });

  useEffect(() => {
    drag(drop(ref));

    // Important : on désactive l'image native du navigateur
    preview(getEmptyImage(), {
      captureDraggingState: true,
    });
  }, [drag, drop, preview]);

  return (
    <div
      ref={ref}
      style={{
        opacity: isDragging ? 0.25 : 1,
      }}
    >
      {children}
    </div>
  );
};


const CardDragPreview = () => {
  const {
    isDragging,
    item,
    currentOffset,
  } = useDragLayer((monitor) => ({
    item: monitor.getItem() as
      | {
          previewData?: DragPreviewData;
        }
      | null,
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging || !currentOffset || !item?.previewData) {
    return null;
  }

  const { libelle, jour, image, nbNuits } = item.previewData;

  return (
    <div
      className="
        pointer-events-none
        fixed
        left-0
        top-0
        z-[99999]
      "
      style={{
        transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
      }}
    >
      <div
        className="
          flex
          items-center
          gap-4
          w-[360px]
          rounded-[24px]
          border
          border-white/80
          bg-[linear-gradient(135deg,rgba(255,255,255,0.98)_0%,rgba(255,246,252,0.94)_48%,rgba(248,243,255,0.98)_100%)]
          px-4
          py-3.5
          shadow-[0_18px_44px_rgba(124,58,237,0.24),0_8px_20px_rgba(236,72,153,0.16)]
        "
      >
        <div
          className="
            flex
            h-[34px]
            w-[34px]
            shrink-0
            items-center
            justify-center
            rounded-full
            bg-[#DDD6FE]
            text-xs
            font-bold
            text-violet-700
          "
        >
          {nbNuits}N
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[11px] font-semibold text-violet-500">
            {jour}
          </div>

          <div className="truncate text-sm font-bold text-neutral-900">
            {libelle}
          </div>
        </div>

      </div>
    </div>
  );
};


const PlanificateurResume: FC<PlanificateurResumeProps> = ({
  categoryCardType = "card3",
  voyageKey,
  emailPartage,
  codePartage,
  isModif,
  onChangeModif,
  mailKo,
  eventCategoriesPage,
  id_pays,
  libelles_pays,
  nbJours,
  visibilite,
  etatVoyage,
  nb_voyageurs,
  monnaie,
  symbole_monnaie,
  numDroit,
  setEventCategoriesPage,
  username,
  affichageBudget,
  nb_likes,
  liked,
  onMajLike,
  onMajDislike,
  verrouillageModif
}) => {
  const { t } = useTranslation();

  const [eventCategories, setEventCategories] = useState<JourneeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number>(0);
  const [selectedJournee, setSelectedJournee] = useState<number | null>(null);
  const [listePec, setListePec] = useState<PecType[]>([]);


  const [lieu1, setLieu1] = useState("");
  const [lieu2, setLieu2] = useState("");
  const [latitude1, setLatitude1] = useState("");
  const [latitude2, setLatitude2] = useState("");
  const [longitude1, setLongitude1] = useState("");
  const [longitude2, setLongitude2] = useState("");
  
  const [isModalConvertisseurOpen, setIsModalConvertisseurOpen] = useState(false);
  const [isModalStatistiquesOpen, setIsModalStatistiquesOpen] = useState(false);
  const [isModalContenuBagageOpen, setIsModalContenuBagageOpen] = useState(false);
  const [isModalImportOpen, setIsModalImportOpen] = useState(false);
  const [isModalExportOpen, setIsModalExportOpen] = useState(false);
  const [isModalLieuxResumeOpen, setIsModalLieuxResumeOpen] = useState(false);
  const [isModalModifResumeOpen, setIsModalModifResumeOpen] = useState(false);
  const [isModalIntegrationOpen, setIsModalIntegrationOpen] = useState(false);
  const [isModalPartageOpen, setIsModalPartageOpen] = useState(false);
  const [isModalEditionPDFOpen, setIsModalEditionPDFOpen] = useState(false);

  const [isModalBookingOpen, setIsModalBookingOpen] = useState(false);

  const [idEvenementForModal, setIdEvenementForModal] = useState<number>(0);
  const [typeEvenementForModal, setTypeEvenementForModal] = useState("");

  const [statNbEtapes, setStatNbEtapes] = useState("");
  const [statBudget, setStatBudget] = useState("");
  const [statDistance, setStatDistance] = useState("");
  const [statDistanceAvion, setStatDistanceAvion] = useState("");
  const [statDistanceBateau, setStatDistanceBateau] = useState("");
  const [statDistanceTrain, setStatDistanceTrain] = useState("");
  const [statDistanceBus, setStatDistanceBus] = useState("");
  const [statDistanceTaxi, setStatDistanceTaxi] = useState("");
  const [statDistanceVoiture, setStatDistanceVoiture] = useState("");
  const [statDistanceMoto, setStatDistanceMoto] = useState("");
  const [statDistanceVelo, setStatDistanceVelo] = useState("");
  const [statDistancePied, setStatDistancePied] = useState("");

  const [groupedNights, setGroupedNights] = useState<any[]>([]);
  const [flags, setFlags] = useState<Flags>({});
  const [lieuxVisites, setLieuxVisites] = useState<Array<string>>([]);
  const [nuitsModif, setNuitsModif] = useState<Array<EventType>>([]);
  const [paysModif, setPaysModif] = useState<string>("");
  const [firstDayModif, setFirstDayModif] = useState<string>("");
  const [firstDayIdModif, setFirstDayIdModif] = useState<number>(0);
  const [initialStartIndexModif, setInitialStartIndexModif] = useState(0);
  const [initialNbNuitsModif, setInitialNbNuitsModif] = useState(0);
  const [isAI, setIsAI] = useState(false);
  

  const [nbLikes, setNbLikes] = useState<number>(nb_likes || 0);

  const [isAdding, setIsAdding] = useState(false);

  // Fonction pour obtenir la valeur d'un cookie
  const getCookie = (name: string) => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(name))
      ?.split('=')[1];
    return cookieValue || '';
  };


const [isLiked, setIsLiked] = useState(() => {

if(liked === 'O')
{
  return true;
}
else
{
  // Vérifie si le cookie existe
  const cookieName = `tripkygoLike${voyageKey}`;
  const cookieValue = getCookie(cookieName);

  // Si le cookie existe et a la valeur 'O', met à jour isLiked à true
  if (cookieValue === 'O') {
    return true;
  }
}
return false;
});

const getRestCountriesCode = (codePays: string) => {
  if (["ww", "xx", "yy", "zz", "WW", "XX", "YY", "ZZ"].includes(codePays)) {
    return "gb";
  }

  return codePays;
};

const handleDragAttemptWithoutModif = (
  e: React.DragEvent<HTMLDivElement>
) => {
  e.preventDefault();
  e.stopPropagation();

  if (!isModif && numDroit === 1) {
    alert("Pour déplacer un élément, veuillez passer en mode modification.");
  }
};

const handleIAClick = () => {
  if(etatVoyage !== 0)
  {
    setIsAI(true);
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}
     
   // Fonction de gestion du clic sur le bouton like
   const handleLikeClick = useCallback(async () => {
    var etatLike=isLiked;
  
    setNbLikes((prevNbLikes) => (etatLike ? prevNbLikes - 1 : prevNbLikes + 1));
  
    // Mise à jour de l'état local
    setIsLiked((prevIsLiked) => !prevIsLiked);
  
    if(!etatLike)
    {
      // Ajout du cookie
      const cookieName = `tripkygoLike${voyageKey}`;
      document.cookie = `${cookieName}=O; path=/`;
  
      const xsrfToken = localStorage.getItem('xsrfToken');
  
      onMajLike(); 

        try{
          const response = await voyageService.likeVoyage({ id_voyage: voyageKey }, {
            headers: {
              'X-XSRF-Token': xsrfToken,
            },
          })
          
        } catch (error: any) {
          console.error("Error like voyage :", error);
        } finally {
        
        }
  
    }
    else
    {
      // Suppression du cookie
    const cookieName = `tripkygoLike${voyageKey}`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  
    const xsrfToken = localStorage.getItem('xsrfToken');

    onMajDislike();

      try{
        const response = await voyageService.dislikeVoyage(voyageKey, {
          headers: {
            'X-XSRF-Token': xsrfToken,
          },
        })
        
      } catch (error: any) {
        console.error("Error dislike voyage:", error);
      } finally {
      
      }
  
    }
  
    
  }, [isLiked, voyageKey]);
  
  
  const toggleModif = () => {
    onChangeModif();
  };
  
  type OnAddEvenementType = (idJournee: number, idEvenement: number, libelleLieu: string, description: string, latitude: string, longitude: string) => number;


  let navigate=useNavigate();

  const handleElementChange = useCallback(
    (journeeId: number, eventId: number, typeEvenement: string, valeur: string, typeElement: string) => {
      setEventCategories((prevCategories) => {
        return prevCategories.map((day) => {
          if (day.id_journee === journeeId) {
            return {
              ...day,
              evenements: day.evenements.map((event) => {
                if (
                  (typeEvenement === 'lieu' && event.typeEv === 'L' && event.id === eventId) ||
                  (typeEvenement === 'trajet' && event.typeEv === 'T' && event.id === eventId) ||
                  (typeEvenement === 'nuit' && event.typeEv === 'N' && event.id === eventId)
                ) {
                  if (typeElement === 'libelle1') {
                    return { ...event, libelle1: valeur };
                  } else if (typeElement === 'libelle2') {
                    return { ...event, libelle2: valeur };
                  } else if (typeElement === 'prix') {
                    return { ...event, prix: valeur };
                  } else if (typeElement === 'paiement') {
                    return { ...event, paiement: valeur };
                  } else if (typeElement === 'acompte_hebergement') {
                    return { ...event, acompte_hebergement: valeur };
                  } else if (typeElement === 'date_paiement_hebergement') {
                    return { ...event, date_paiement_hebergement: valeur };
                  } else if (typeElement === 'debut') {
                    return { ...event, debut: valeur };
                  } else if (typeElement === 'fin') {
                    return { ...event, fin: valeur };
                  } else if (typeElement === 'compagnie') {
                    return { ...event, compagnie: valeur };
                  } else if (typeElement === 'ref') {
                    return { ...event, ref: valeur };
                  } else if (typeElement === 'distance') {
                    return { ...event, distance: valeur };
                  } else if (typeElement === 'duree') {
                    return { ...event, duree: valeur };
                  } else if (typeElement === 'invisible1') {
                    return { ...event, invisible1: valeur };
                  } else if (typeElement === 'invisible2') {
                    return { ...event, invisible2: valeur };
                  } else if (typeElement === 'type') {
                    return { ...event, type: valeur };
                  } else if (typeElement === 'moment') {
                    return { ...event, moment: valeur };
                  } else if (typeElement === 'rattachement') {
                    return { ...event, rattachement: valeur };
                  } else if (typeElement === 'image') {
                    return { ...event, image: valeur };
                  } else if (typeElement === 'lat') {
                    return { ...event, lat: valeur };
                  } else if (typeElement === 'longi') {
                    return { ...event, longi: valeur };
                  } else if (typeElement === 'lat2') {
                    return { ...event, lat2: valeur };
                  } else if (typeElement === 'longi2') {
                    return { ...event, longi2: valeur };
                  } else if (typeElement === 'place') {
                    return { ...event, place: valeur };
                  } else if (typeElement === 'latModif') {
                    return { ...event, latModif: valeur };
                  } else if (typeElement === 'longiModif') {
                    return { ...event, longiModif: valeur };
                  } else if (typeElement === 'lat2Modif') {
                    return { ...event, lat2Modif: valeur };
                  } else if (typeElement === 'longi2Modif') {
                    return { ...event, longi2Modif: valeur };
                  } else if (typeElement === 'document') {
                    return { ...event, document: valeur };
                  } else if (typeElement === 'lien') {
                    return { ...event, lien: valeur };
                  } else if (typeElement === 'bloc') {
                    return { ...event, bloc: valeur };
                  } else if (typeElement === 'pays') {
                    return { ...event, pays: valeur };
                  } else if (typeElement === 'nom_pays') {
                    return { ...event, nom_pays: valeur };
                  }
                }
                return event;
              }),
            };
          }
          return day;
        });
      });
    },
    [] // dependencies, update if needed
  );

  const handleApplyAIActions = async (actions: any[]) => {
  const verrouillageResultat = await verrouillageModif();

  if (verrouillageResultat !== 1) {
    return;
  }

  try {
    setIsAdding(true);

     const rebalanceActions = actions.filter(
          (action) => action.type === "SUGGEST_REBALANCE"
        );
    
        const otherActions = actions.filter(
          (action) => action.type !== "SUGGEST_REBALANCE"
        );
    
        if (rebalanceActions.length > 0) {
          const response = await aiService.applyPlannerActions(
            voyageKey,
            rebalanceActions
          );
    
          const updatedVoyage = response.data?.voyage;
    
          if (updatedVoyage?.groupedResults) {
            setEventCategories(updatedVoyage.groupedResults);
          }
        }
    
        for (const action of otherActions) {
      switch (action.type) {
        case "ADD_ACTIVITY":
          await applyAIAddActivity(action);
          break;

        case "ADD_TRANSPORT":
          await applyAIAddTransport(action);
          break;

        case "UPDATE_TRANSPORT":
          await applyAIUpdateTransport(action);
          break;

        default:
          console.log("Action IA non gérée :", action);
      }
    }
  } catch (error) {
    console.error("Erreur application actions IA :", error);
    alert("Une erreur est survenue lors de l'application des changements IA.");
  } finally {
    setIsAdding(false);
  }
};

const getDayByNumber = (dayNumber: number) => {
  return eventCategories.find((day) => day.num_jour === dayNumber);
};

const getInsertAnchorEvent = (day: JourneeType) => {
  const nightEvent = day.evenements.find((event) => event.typeEv === "N");

  if (nightEvent) {
    return nightEvent;
  }

  return day.evenements[day.evenements.length - 1];
};

const applyAIAddActivity = async (action: any) => {
  if (!action.dayNumber) {
    throw new Error("Jour manquant pour l'activité IA");
  }

  const day = getDayByNumber(Number(action.dayNumber));

  if (!day) {
    throw new Error(`Journée introuvable : ${action.dayNumber}`);
  }

  const anchorEvent = getInsertAnchorEvent(day);

  if (!anchorEvent) {
    throw new Error(`Aucun point d'insertion trouvé pour le jour ${action.dayNumber}`);
  }

  let typeEvenement = "lieu";
  if (action.typeRepas === "O")
  {
    typeEvenement="repas";
  }

  const response = await voyageService.ajouterEvenement(voyageKey, {
    type: typeEvenement,
    journee: day.id_journee,
    idEvenement: anchorEvent.id,
    typeEvenement: anchorEvent.typeEv,
  });

  const apiResult = response.data;

  if (apiResult === "Erreur" || !apiResult.message) {
    throw new Error("Erreur lors de la création de l'activité IA");
  }

  const newEventId = Number(apiResult.message);

  const title = action.title || "";
  const description = action.description || "";
  const latitude = action.lat || "";
  const longitude = action.longi || "";
  const place = action.place || "";

  await voyageService.modifierEvenement(voyageKey, {
    typeEvenement: "lieu",
    idEvenement: newEventId,
    colonne: "libelle_lieu",
    contenu: title,
    colonne1: "lat_lieu",
    contenu1: latitude,
    colonne2: "long_lieu",
    contenu2: longitude,
    placeId: place,
  });

  if (description) {
    await voyageService.modifierEvenement(voyageKey, {
      typeEvenement: "lieu",
      idEvenement: newEventId,
      colonne: "activites",
      contenu: description,
      conservation: false,
    });
  }

  setEventCategories((prevCategories) =>
    produce(prevCategories, (draft : any) => {
      const draftDay = draft.find((item : any) => item.id_journee === day.id_journee);

      if (!draftDay) return;

      const anchorIndex = draftDay.evenements.findIndex(
        (event : any) => event.id === anchorEvent.id && event.typeEv === anchorEvent.typeEv
      );

      const insertIndex =
        anchorIndex >= 0 ? anchorIndex : draftDay.evenements.length;

      draftDay.evenements.splice(insertIndex, 0, {
        typeEv: "L",
        id: newEventId,
        ordre: anchorEvent.ordre || 0,
        libelle1: title,
        libelle2: description,
        prix: action.price ? String(action.price) : "",
        paiement: "",
        lat: latitude,
        longi: longitude,
        place: place,
        bloc: "",
        type: "",
        lien: "",
        etat_lien: "",
        lien_modifie: "",
        ref: "",
        compagnie: "",
        debut: "",
        fin: "",
        lat2: "",
        longi2: "",
        distance: "",
        duree: "",
        location: "",
        moment: "",
        rattachement: "",
        invisible1: "",
        invisible2: "",
        date_paiement_hebergement: "",
        acompte_hebergement: "",
        image: "",
        document: "",
      } as any);
    })
  );
};

const getAIInsertAnchorEvent = (day: JourneeType, action: any) => {
  if (action.insertBeforeEventId && action.insertBeforeEventType) {
    const foundEvent = day.evenements.find(
      (event : any) =>
        event.id === Number(action.insertBeforeEventId) &&
        event.typeEv === action.insertBeforeEventType
    );

    if (foundEvent) {
      return foundEvent;
    }
  }

  return getInsertAnchorEvent(day);
};

const applyAIAddTransport = async (action: any) => {
  if (!action.dayNumber) {
    throw new Error("Jour manquant pour le trajet IA");
  }

  const day = getDayByNumber(Number(action.dayNumber));

  if (!day) {
    throw new Error(`Journée introuvable : ${action.dayNumber}`);
  }

  const anchorEvent = getAIInsertAnchorEvent(day, action);

  if (!anchorEvent) {
    throw new Error(`Aucun point d'insertion trouvé pour le jour ${action.dayNumber}`);
  }

  const response = await voyageService.ajouterEvenement(voyageKey, {
    type: "trajet",
    journee: day.id_journee,
    idEvenement: anchorEvent.id,
    typeEvenement: anchorEvent.typeEv,
  });

  const apiResult = response.data;

  if (apiResult === "Erreur" || !apiResult.message) {
    throw new Error("Erreur lors de la création du trajet IA");
  }

  const newEventId = Number(apiResult.message);

  await voyageService.modifierEvenement(voyageKey, {
    typeEvenement: "trajet",
    idEvenement: newEventId,
    colonne: "depart_trajet",
    contenu: action.from || "",
    colonne1: "lat_depart_trajet",
    contenu1: action.fromLat || "",
    colonne2: "long_depart_trajet",
    contenu2: action.fromLongi || "",
  });

  await voyageService.modifierEvenement(voyageKey, {
    typeEvenement: "trajet",
    idEvenement: newEventId,
    colonne: "arrivee_trajet",
    contenu: action.to || "",
    colonne1: "lat_arrivee_trajet",
    contenu1: action.toLat || "",
    colonne2: "long_arrivee_trajet",
    contenu2: action.toLongi || "",
  });

  if (action.transportType) {
    await voyageService.modifierEvenement(voyageKey, {
      typeEvenement: "trajet",
      idEvenement: newEventId,
      colonne: "type_trajet",
      contenu: action.transportType,
    });
  }

  if (action.distance) {
     await voyageService.enregistrerDistance(voyageKey, {
       idEvenement: newEventId,
       distance: action.distance,
       duree: action.duration
     });
   }

  setEventCategories((prevCategories) =>
    produce(prevCategories, (draft : any) => {
      const draftDay = draft.find((item : any) => item.id_journee === day.id_journee);

      if (!draftDay) return;

      const anchorIndex = draftDay.evenements.findIndex(
        (event : any) => event.id === anchorEvent.id && event.typeEv === anchorEvent.typeEv
      );

      const insertIndex =
        anchorIndex >= 0 ? anchorIndex : draftDay.evenements.length;

      draftDay.evenements.splice(insertIndex, 0, {
        typeEv: "T",
        id: newEventId,
        ordre: anchorEvent.ordre || 0,
        libelle1: action.from || "",
        libelle2: action.to || "",
        prix: action.price ? String(action.price) : "",
        paiement: "",
        lat: action.fromLat || "",
        longi: action.fromLongi || "",
        place: "",
        bloc: "",
        type: action.transportType || "",
        lien: "",
        etat_lien: "",
        lien_modifie: "",
        ref: "",
        compagnie: "",
        debut: "",
        fin: "",
        lat2: action.toLat || "",
        longi2: action.toLongi || "",
        distance: action.distance || "",
        duree: action.duration || "",
        location: "",
        moment: "",
        rattachement: "",
        invisible1: "",
        invisible2: "",
        date_paiement_hebergement: "",
        acompte_hebergement: "",
        image: "",
        document: "",
      } as any);
    })
  );
};

const applyAIUpdateTransport = async (action: any) => {
  alert(
    "La correction automatique d’un trajet existant sera ajoutée ensuite. Pour l’instant, tu peux utiliser les propositions comme aide à la modification."
  );

  console.log("Correction trajet proposée :", action);
};

  useEffect(() => {

    
    if (JSON.stringify(eventCategories) !== JSON.stringify(eventCategoriesPage)) {
      setEventCategories(eventCategoriesPage);
      setLoading(false); 
    }
  }, [eventCategoriesPage]);

useEffect(() => {
    if (!loading) {
      // Mettre à jour eventCategoriesPage lorsque eventCategories change
      setEventCategoriesPage(eventCategories);
    }
  }, [eventCategories]);

const addUniqueImageItems = (
  target: ResumeImageItem[],
  imageItems: ResumeImageItem[]
) => {
  imageItems.forEach((imageItem) => {
    if (!target.some((targetItem) => targetItem.url === imageItem.url)) {
      target.push(imageItem);
    }
  });
};

useEffect(() => {
  const groups: GroupNuits[] = [];

  let currentGroupPays: GroupNuits | null = null;
  let currentGroupVille: GroupVille | null = null;

  /*
   * On extrait d'abord toutes les nuits affichables dans l'ordre du voyage.
   * Cela permet de rechercher le pays suivant lorsqu'une nuit en transport
   * se trouve au début de l'itinéraire.
   */
  const orderedNights: Array<{
    day: JourneeType;
    evenement: EventType;
  }> = [];

	  eventCategories.forEach((day) => {
	    day.evenements.forEach((evenement) => {
      const isVisibleNight =
        evenement.typeEv === "N" &&
        (
          isModif ||
          evenement.libelle1 === "Nuit en transport" ||
          evenement.libelle1 === "Night in transport" ||
          (evenement.nom_pays !== null && evenement.nom_pays !== "")
        );

      if (isVisibleNight) {
        orderedNights.push({
          day,
          evenement,
        });
      }
    });
  });

  const isTransportNight = (evenement: EventType) =>
	    evenement.libelle1 === "Nuit en transport" ||
	    evenement.libelle1 === "Night in transport";

  const getTransportTypeForNight = (
    day: JourneeType,
    night: EventType
  ): string | null => {
    const trajets = day.evenements.filter(
      (event) => event.typeEv === "T" && event.type
    );
    const overnightTrajet = [...trajets]
      .reverse()
      .find((event) => event.invisible2 === "O");

    if (overnightTrajet?.type) return overnightTrajet.type;

    const precedingTrajet = [...trajets]
      .reverse()
      .find((event) => event.ordre <= night.ordre);

    return precedingTrajet?.type || null;
  };

  const getResumeImageItemsFromDay = (day: JourneeType): ResumeImageItem[] => {
    return day.evenements
      .filter(
        (event) =>
          (event.typeEv === "L" || event.typeEv === "N") &&
          event.image &&
          event.image !== ""
      )
      .map((event) => {
        const libelle1 = event.libelle1 || "";
        const libelle2 = event.libelle2 || "";

        if (event.typeEv === "N") {
          const nightLabel = [libelle1, libelle2].filter(Boolean).join(" - ");

          return {
            url: event.image as string,
            caption: `Nuit ${day.num_jour}${nightLabel ? ` · ${nightLabel}` : ""}`,
          };
        }

        return {
          url: event.image as string,
          caption: `Jour ${day.num_jour} · ${libelle1}`,
        };
      });
  };

  /*
   * Recherche du prochain vrai pays après une nuit en transport.
   */
  const findNextCountry = (
    currentIndex: number
  ): {
    pays: string;
    nom_pays: string;
  } | null => {
    for (
      let nextIndex = currentIndex + 1;
      nextIndex < orderedNights.length;
      nextIndex++
    ) {
      const nextNight = orderedNights[nextIndex].evenement;

      if (isTransportNight(nextNight)) {
        continue;
      }

      const nextCountryCode = nextNight.pays || "";
      const nextCountryName =
        nextNight.nom_pays || t("nuitsIncompletes");

      return {
        pays: nextCountryCode,
        nom_pays: nextCountryName,
      };
    }

    return null;
  };

  orderedNights.forEach(({ day, evenement }, nightIndex) => {
    const isNuitTransport = isTransportNight(evenement);
    const typeTransport = isNuitTransport
      ? getTransportTypeForNight(day, evenement)
      : null;

    let codePays = "";
    let paysNom = "";

    if (isNuitTransport) {
      if (currentGroupPays) {
        /*
         * Une nuit en transport située après un pays reste rattachée
         * au pays courant.
         */
        codePays = currentGroupPays.pays;
        paysNom = currentGroupPays.nom_pays;
      } else {
        /*
         * Si la nuit en transport est le premier élément,
         * on la rattache au prochain pays trouvé.
         */
        const nextCountry = findNextCountry(nightIndex);

        if (nextCountry) {
          codePays = nextCountry.pays;
          paysNom = nextCountry.nom_pays;
        } else {
          /*
           * Sécurité : s'il n'existe aucun pays après,
           * on conserve une colonne Trajet.
           */
          codePays = "";
          paysNom = t("trajet");
        }
      }
    } else {
      codePays = evenement.pays || "";
      paysNom = evenement.nom_pays || t("nuitsIncompletes");
    }

    const paysGroupKey =
      codePays !== ""
        ? codePays.toLowerCase()
        : paysNom;

    const currentGroupKey =
      currentGroupPays?.pays && currentGroupPays.pays !== ""
        ? currentGroupPays.pays.toLowerCase()
        : currentGroupPays?.nom_pays;

    if (!currentGroupPays || currentGroupKey !== paysGroupKey) {
      currentGroupPays = {
        nom_pays: paysNom,
        pays: codePays,
        groupVilles: [],
      };

      groups.push(currentGroupPays);
      currentGroupVille = null;
    }

    const villeNom =
      evenement.libelle1 || t("cliquezCompleter");

    const resumeImageItems = getResumeImageItemsFromDay(day);
    const resumeImages = resumeImageItems.map((imageItem) => imageItem.url);

    /*
     * Une nuit en transport reste un bloc distinct à l'intérieur
     * de la colonne du pays.
     */
    if (
      !currentGroupVille ||
      currentGroupVille.libelle1 !== villeNom
    ) {
      currentGroupVille = {
        libelle1: villeNom,
        typeTransport,
        lat: evenement.lat,
        longi: evenement.longi,
        firstDay: day.date_journee.split(" ")[1],
        firstDayId: day.id_journee,
        image: resumeImages[0] || "",
        images: resumeImages,
        imageItems: resumeImageItems,
        nights: [evenement],
      };

      currentGroupPays.groupVilles.push(currentGroupVille);
    } else {
      currentGroupVille.nights.push(evenement);
      addUniqueImageItems(currentGroupVille.imageItems, resumeImageItems);
      currentGroupVille.images = currentGroupVille.imageItems.map(
        (imageItem) => imageItem.url
      );
    }

    if (!currentGroupVille.image && currentGroupVille.images.length > 0) {
      currentGroupVille.image = currentGroupVille.images[0];
    }
  });

  setGroupedNights(groups);
}, [eventCategories, isModif, t]);
  
useEffect(() => {
  groupedNights.forEach((group) => {
    const countryCode = group.pays;

    if (!countryCode) return;

    if (countryCode in flags) return;

    const flagCode = getRestCountriesCode(countryCode).toLowerCase();

    setFlags((prevFlags) => ({
      ...prevFlags,
      [countryCode]: `https://flagcdn.com/w40/${flagCode}.png`,
    }));
  });
}, [groupedNights, flags]);
    
  useEffect(() => {
    const updatedListePec = eventCategories.flatMap((day) =>
    day.evenements
      .filter(
        (event) =>
          event.location === 'O' &&
          event.moment === 'P'
      )
      .map((event) => ({
        date_journee: day.date_journee,
        id_journee: day.id_journee,
        id_evenement: event.id, 
        type_transport: event.type || "",
        rattachement: event.rattachement || "",
        debut: event.debut || ""
      }))
  );

  setListePec(updatedListePec);

  }, [eventCategories]);


  if (loading) {
    return <div className="text-center mt-20 mb-20"><h1 className="text-xl">{t('chargementVoyage')}</h1></div>;
  }


  const closeModal = () => {
    setSelectedEvent(null);
    setSelectedEventIndex(0);
    setSelectedJournee(null);
    setIsModalOpen(false);
  };

  const openModalConvertisseur = () => {
    setIsModalConvertisseurOpen(true);
  };

  const closeModalConvertisseur = () => {
    setIsModalConvertisseurOpen(false);
  };

  
  const openModalBooking = (typeEvenement: string, idEvenement: number) => {
    setTypeEvenementForModal(typeEvenement);
    setIdEvenementForModal(idEvenement);
    setIsModalBookingOpen(true);
  };

  const closeModalBooking = () => {
    setLatitude1("");
    setLatitude2("");
    setLongitude1("");
    setLongitude2("");
    setTypeEvenementForModal("");
    setIdEvenementForModal(0);

    setIsModalBookingOpen(false);
  };

  const openModalLieuxResume = () => {
    setIsModalLieuxResumeOpen(true);
  };

  const closeModalLieuxResume = () => {
    setIsModalLieuxResumeOpen(false);
  };

  const openModalModifResume = () => {
    setIsModalModifResumeOpen(true);
  };

  const closeModalModifResume = () => {
    setIsModalModifResumeOpen(false);
  };
  
  const openModalStatistiques = () => {
    setIsModalStatistiquesOpen(true);
  };

  const closeModalStatistiques = () => {
    setIsModalStatistiquesOpen(false);
  };
  
  const openModalContenuBagage = () => {
    setIsModalContenuBagageOpen(true);
  };

  const closeModalContenuBagage = () => {
    setIsModalContenuBagageOpen(false);
  };

  const closeModalImport = () => {
    setIsModalImportOpen(false);
  };

  const openModalImport = () => {
    setIsModalImportOpen(true);
  };

  const closeModalIntegration = () => {
    setIsModalIntegrationOpen(false);
  };

  const openModalIntegration = () => {
    setIsModalIntegrationOpen(true);
  };

  const closeModalPartage = () => {
    setIsModalPartageOpen(false);
  };

  const openModalPartage = () => {
    setIsModalPartageOpen(true);
  };


  const closeModalExport = () => {
    setIsModalExportOpen(false);
  };

  const openModalExport = () => {
    setIsModalExportOpen(true);
  };

  const openModalEditionPDF = () => {
    setIsModalEditionPDFOpen(true);
  };

  const closeModalEditionPDF = () => {
    setIsModalEditionPDFOpen(false);
  };

  const handleAddEvenement = (idJournee: number, idEvenement: number, libelleLieu: string, description: string, latitude: string, longitude: string, placeId: string): number  => {
  
    const modifVoyage = async () => {
      try {

      setIsAdding(true);

      const day = eventCategories.find((day) => day.id_journee === idJournee);
      let foundEvent: EventType | null = null;
      let eventIndex: number = -1;
    
      if (day) {
        const index = day.evenements.findIndex((event: EventType) => event.id === idEvenement);
    
        if (index !== -1) {
          eventIndex=index;
          foundEvent = day.evenements[index];
        } 
      }

      const type='lieu';
  
        if (foundEvent !== null && eventIndex !== null && eventIndex !== -1) {
          
          // Effectuez la requête API pour ajouter un événement
            voyageService.ajouterEvenement(voyageKey, {
                type,
                journee: idJournee,
                idEvenement: foundEvent.id,
                typeEvenement: foundEvent.typeEv
            })
          .then((response: any) => {
    
            const apiResult = response.data;
    
      
            // Vérifiez si la réponse de l'API est "Erreur"
            if (apiResult !== "Erreur") {
    
              // Fermez la modale
              closeModal();
      
              setEventCategories((prevCategories) => {
                // Trouvez la journée correspondante dans la liste
                const updatedCategories = prevCategories.map((day) => {
                  if (day.id_journee === idJournee) {
                    
                    // Créez une copie du tableau des événements
                    const updatedEvenements = [...day.evenements];

                    // Ajoutez la CardLieuModifie au-dessus du bon événement
                    updatedEvenements.splice(eventIndex, 0, {
                      typeEv: 'L',
                      id: apiResult.message, // Utilisez le résultat de l'API comme ID de l'événement
                      ordre: foundEvent?.ordre || 0,
                  libelle1: "",
                  libelle2: "",
                  prix: "",
                  paiement: "",
                  lat: "",
                  longi: "",
                  place: "",
                  bloc: "",
                  type: '',
                  lien: "",
                  etat_lien: "",
                  lien_modifie: "",
                  ref: "",
                  compagnie: "",
                  debut: "",
                  fin: "",
                  lat2: "",
                  longi2: "",
                  distance: "",
                  duree: "",
                  location: '',
                  moment: '',
                  rattachement: "",
                  invisible1: "",
                  invisible2: "",
                  date_paiement_hebergement: "",
                  acompte_hebergement: "",
                  image: "",
                  document: ""
                });

                // Retourner une copie de l'objet avec les événements mis à jour
                return {
                  ...day,
                  evenements: updatedEvenements,
                };
    
                         }
                        return day;
                      });
            
                      return updatedCategories;
                    });
    
                  } else {
                    setIsAdding(false);
                    console.error("Erreur lors de la requête API");
                    return -1
                    // Gérez l'erreur, affichez un message, etc.
                  }
    
                  const idNouvLieu=apiResult.message ? parseInt(apiResult.message, 10) : 0;

            
                  if (idNouvLieu !== 0) {

                    // Utilisez await pour attendre que modifierLieu soit terminé
                    modifierLieu(idJournee, idNouvLieu, libelleLieu, description, latitude, longitude, placeId);
                    setIsAdding(false);
                    return 0;
                  }
                  else
                  {
                    setIsAdding(false);
                  }
            })
          .catch((error: any) => {
            setIsAdding(false);
            if (error.response?.status === 401) {
              alert("Erreur lors de l'ajout d'évenement, vous n'êtes plus connecté(e).")
              localStorage.removeItem('xsrfToken');
              navigate('/login');
            }
            else
            {
              alert("Erreur lors de l'ajout d'évenement");
              return -1;
            }
            console.error("Erreur lors de l'ajout d'évenement:", error);
          });
      
          } 
          else
          {
            setIsAdding(false);
          }
    } catch (error) {
      setIsAdding(false);
      console.error("Une erreur s'est produite :", error);
      alert("Une erreur s'est produite lors de la manipulation des événements.");
      return -1;
    }
  };

  const processModifVoyage = async () => {
    
    const verrouillageResultat = await verrouillageModif();

    if (verrouillageResultat === 1) {
      modifVoyage();
    }
  };

  processModifVoyage();
    return 0;
  };  

  const modifierLieu = (idJournee : number, idEvenementNouv : number , libelleLieu : string, description : string, latitude : string, longitude : string, placeId: string) => {

      handleElementChange(idJournee, idEvenementNouv, 'lieu', libelleLieu, 'libelle1');
      handleElementChange(idJournee, idEvenementNouv, 'lieu', latitude, 'lat');
      handleElementChange(idJournee, idEvenementNouv, 'lieu', longitude, 'longi');
      handleElementChange(idJournee, idEvenementNouv, 'lieu', placeId, 'place');

      if(description !== null && description !== "")
      {
        handleElementChange(idJournee, idEvenementNouv, 'lieu', description, 'libelle2');
      }
         
      //enregistrement de la modification
      voyageService.modifierEvenement(voyageKey, {
        typeEvenement: 'lieu',
        idEvenement: idEvenementNouv,
        colonne: "libelle_lieu",
        contenu: libelleLieu,
        colonne1: 'lat_lieu',
        colonne2: 'long_lieu',
        contenu1: latitude,
        contenu2: longitude,
        placeId: placeId
      })
      .then((response: any) => {
        
        if(description !== null && description !== "")
        {
          //enregistrement de la modification
          voyageService.modifierEvenement(voyageKey, {
            typeEvenement: 'lieu',
            idEvenement: idEvenementNouv,
            colonne: "activites",
            contenu: description,
            conservation: false
          })
          .then((response: any) => {

            
          })
          .catch((error: any) =>
          {
            if (error.response?.status === 401) {
              alert("Erreur lors de l'enregistrement de la modification, vous n'êtes plus connecté(e).")
              localStorage.removeItem('xsrfToken');
              navigate('/login');
            }
            else
            {
              alert("Erreur lors de l'enregistrement de la modification de l'évenement");
            }
          });
        }
      })
      .catch((error: any) =>
      {
        if (error.response?.status === 401) {
          alert("Erreur lors de l'enregistrement de la modification, vous n'êtes plus connecté(e).")
          localStorage.removeItem('xsrfToken');
          navigate('/login');
        }
        else
        {
          alert("Erreur lors de l'enregistrement de la modification de l'évenement");
        }
      });
  };

        const moveCard = async (dragIndex: number, hoverIndex: number, draggedIdJournee: number | null, id_journee: number, index: number) => {

          const modifVoyage = async () => {

            try {
          if (draggedIdJournee !== null && id_journee !== null  && draggedIdJournee !== id_journee) {

            const updatedCategories = [...eventCategories];
            var indexDeb1 = eventCategories.findIndex(day => day.id_journee === draggedIdJournee);
            var indexDeb2 = eventCategories.findIndex(day => day.id_journee === id_journee);
    
    
            var nbAj=0;
            var nbSou=0;
            let indexGroupeNuit1 = -1;
            let indexGroupeNuit2 = -1;
            let indexFin1 = -1;
            let indexFin2 = -1;
            var numNuitDebAj=0;
            var numNuitFinAj=0;
            var numNuitDebSou=0;
            var numNuitFinSou=0;

            for(var i=0; i < groupedNights.length; i++)
            {
              for(var j=0; j < groupedNights[i].groupVilles.length; j++)
              {
                if(groupedNights[i].groupVilles[j].firstDayId === draggedIdJournee)
                {
                  indexFin1=indexDeb1+groupedNights[i].groupVilles[j].nights.length-1;
                  indexGroupeNuit1=i;
                }
                else if(groupedNights[i].groupVilles[j].firstDayId === id_journee)
                {
                  indexFin2=indexDeb2+groupedNights[i].groupVilles[j].nights.length-1;
                  indexGroupeNuit2=i;
                }
              }
            }

            if (
            indexDeb1 === -1 ||
            indexDeb2 === -1 ||
            indexFin1 === -1 ||
            indexFin2 === -1
          ) {
           
            alert(
              "Impossible de retrouver les journées correspondant aux villes déplacées, veuillez actualiser la page."
            );

            return;
          }
 /*
 * Le calcul dépend uniquement de la position des blocs,
 * pas du pays auquel ils appartiennent.
 *
 * Si la ville déplacée est avant la ville cible :
 * [ville déplacée] [éléments intermédiaires + ville cible]
 * devient :
 * [éléments intermédiaires + ville cible] [ville déplacée]
 *
 * Si la ville déplacée est après la ville cible :
 * [ville cible + éléments intermédiaires] [ville déplacée]
 * devient :
 * [ville déplacée] [ville cible + éléments intermédiaires]
 */
if (indexDeb1 < indexDeb2) {
  /*
   * Bloc 1 : ville déplacée.
   */
  numNuitDebAj = indexDeb1 + 1;
  numNuitFinAj = indexFin1 + 1;

  /*
   * Bloc 2 : tout ce qui suit la ville déplacée,
   * jusqu'à la fin de la ville cible incluse.
   *
   * Cela fonctionne aussi quand les deux villes sont contiguës.
   */
  numNuitDebSou = indexFin1 + 2;
  numNuitFinSou = indexFin2 + 1;
} else {
  /*
   * Bloc 1 : depuis la ville cible jusqu'à la journée
   * précédant la ville déplacée.
   */
  numNuitDebAj = indexDeb2 + 1;
  numNuitFinAj = indexDeb1;

  /*
   * Bloc 2 : ville déplacée.
   */
  numNuitDebSou = indexDeb1 + 1;
  numNuitFinSou = indexFin1 + 1;
}

nbAj = numNuitFinSou - numNuitDebSou + 1;
nbSou = numNuitFinAj - numNuitDebAj + 1;

                           const rangesAreValid =
                      nbAj > 0 &&
                      nbSou > 0 &&
                      numNuitDebAj > 0 &&
                      numNuitFinAj >= numNuitDebAj &&
                      numNuitDebSou > 0 &&
                      numNuitFinSou >= numNuitDebSou;

            if (rangesAreValid)
            {
                voyageService.echangerNuits(voyageKey, {
                  nbAj,
                  nbSou,
                  numNuitDebAj,
                  numNuitDebSou,
                  numNuitFinAj,
                  numNuitFinSou
                })
                .then((response: any) => {

                  for (var i = numNuitDebAj - 1; i <= numNuitFinSou - 1; i++) {
                    // Create a deep copy of the day object to avoid mutation
                    const eventCategoryCopy = { ...eventCategories[i] };
                  
                    var dateJournee = eventCategoryCopy.date_journee.split(" ")[1];
                  
                    if (i < numNuitDebSou - 1) {
                      // Modify the deep copy
                      eventCategoryCopy.date_journee = addDays(dateJournee, nbAj);
                      eventCategoryCopy.num_jour = eventCategoryCopy.num_jour + nbAj;
                    } else {
                      eventCategoryCopy.date_journee = subtractDays(dateJournee, nbSou);
                      eventCategoryCopy.num_jour = eventCategoryCopy.num_jour - nbSou;
                    }
                  
                    // Replace the original object with the modified copy
                    eventCategories[i] = eventCategoryCopy;
                  }
                  
                  // Slice the ranges
                  const range1 = eventCategories.slice(numNuitDebAj - 1, numNuitFinAj);
                  const range2 = eventCategories.slice(numNuitDebSou - 1, numNuitFinSou);
                  
                  // Merge the arrays
                  const mergedArray = [...range2, ...range1];
                  
                  var j = 0;
                  for (var i = numNuitDebAj - 1; i <= numNuitFinSou - 1; i++) {
                    // Ensure immutability when assigning merged data
                    eventCategories[i] = { ...mergedArray[j] }; // Deep copy each item from merged array
                    j++;
                  }
                  
                  // Update the state with the new array
                  setEventCategories([...eventCategories]); // Spread to trigger re-render
                  
                })
                .catch((error: any) => {
                  console.log("error",error);
                  if (error.response?.status === 401) {
                    alert("Erreur lors de l'échange, vous n'êtes pas le créateur du voyage.")
                    navigate('/');
                  }
                  else
                  {
                    alert("Erreur lors de l'échange");
                  }
                })
            }
            else
            {
                          console.log("ERREUR");
              alert("Erreur, lors de l'échange. Veuillez actualiser la page.")
            }
        }
      } catch (error) {
        console.error(error);
        alert("Erreur lors de la modification !");
      }
    };

    const processModifVoyage = async () => {
      
      const verrouillageResultat = await verrouillageModif();
  
      if (verrouillageResultat === 1) {
        modifVoyage();
      }
    };

    processModifVoyage();
      };

function addDays(dateString: string, daysToAdd: number): string {
  // Split the input date string to get day, month, and year
  const [day, month, year] = dateString.split("/");

  // Create a new Date object
  const date = new Date(`${year}-${month}-${day}`);

  // Get the current timestamp and add the required number of days
  const newTimestamp = date.getTime() + (daysToAdd * 24 * 60 * 60 * 1000);

  // Create a new Date from the updated timestamp
  const newDate = new Date(newTimestamp);

  // Get the name of the day in French
  const options = { weekday: 'long' } as const;
  let nomJour = newDate.toLocaleDateString('fr-FR', options);

  // Capitalize the first letter of nomJour
  nomJour = nomJour.charAt(0).toUpperCase() + nomJour.slice(1);

  // Format the date back to dd/mm/yyyy
  const newDay = String(newDate.getDate()).padStart(2, '0'); // Ensure two digits for day
  const newMonth = String(newDate.getMonth() + 1).padStart(2, '0'); // Ensure two digits for month (months are 0-based)
  const newYear = newDate.getFullYear(); // Get the year

  // Return the formatted date with day name
  return `${nomJour} ${newDay}/${newMonth}/${newYear}`;
}

function subtractDays(dateString: string, daysToSubtract: number): string {
  // Split the input date string to get day, month, and year
  const [day, month, year] = dateString.split("/");

  // Create a new Date object
  const date = new Date(`${year}-${month}-${day}`);

  // Get the current timestamp and subtract the required number of days
  const newTimestamp = date.getTime() - (daysToSubtract * 24 * 60 * 60 * 1000);

  // Create a new Date from the updated timestamp
  const newDate = new Date(newTimestamp);

  // Get the name of the day in French
  const options = { weekday: 'long' } as const;
  let nomJour = newDate.toLocaleDateString('fr-FR', options);

  // Capitalize the first letter of nomJour
  nomJour = nomJour.charAt(0).toUpperCase() + nomJour.slice(1);

  // Format the date back to dd/mm/yyyy
  const newDay = String(newDate.getDate()).padStart(2, '0'); // Ensure two digits for day
  const newMonth = String(newDate.getMonth() + 1).padStart(2, '0'); // Ensure two digits for month (months are 0-based)
  const newYear = newDate.getFullYear(); // Get the year

  // Return the formatted date with day name
  return `${nomJour} ${newDay}/${newMonth}/${newYear}`;
}

const echangerNuits = async (idJournee1: number, idJournee2: number, indexGroupe2: number) => {
  const modifVoyage = async () => {
    try {
  var nbNuitsGroupe2=0;
  for(var i=0; i< groupedNights[indexGroupe2].groupVilles.length; i++)
  {
    nbNuitsGroupe2+=groupedNights[indexGroupe2].groupVilles[i].nights.length;
  }


  const indexDeb1 = eventCategories.findIndex(day => day.id_journee === idJournee1);
  const indexDeb2 = eventCategories.findIndex(day => day.id_journee === idJournee2);
  const indexFin1 = indexDeb2-1;
  const indexFin2 = indexDeb2+nbNuitsGroupe2-1;
  const nbNuitsGroupe1=indexFin1-indexDeb1+1;

  // Swap the days in the array
  if (indexDeb1 !== -1 && indexFin1 !== -1 && indexDeb2 !== -1) 
  {
    voyageService.echangerNuits(voyageKey, {
      nbAj:nbNuitsGroupe2,
      nbSou:nbNuitsGroupe1,
      numNuitDebAj:indexDeb1+1,
      numNuitDebSou:indexDeb2+1,
      numNuitFinAj:indexFin1+1,
      numNuitFinSou:indexFin2+1
    })
    .then((response: any) => {
     
        for(var i=indexDeb1; i<=indexFin2; i++)
        {
          var dateJournee=eventCategories[i].date_journee.split(" ")[1];

          if(i < indexDeb2)
          {
            eventCategories[i].date_journee=addDays(dateJournee, nbNuitsGroupe2);
            eventCategories[i].num_jour=eventCategories[i].num_jour+nbNuitsGroupe2;
          }
          else
          {
            eventCategories[i].date_journee=subtractDays(dateJournee, nbNuitsGroupe1);
            eventCategories[i].num_jour=eventCategories[i].num_jour-nbNuitsGroupe1;
          }
        }

        const range1 = eventCategories.slice(indexDeb1, indexFin1 + 1);
        const range2 = eventCategories.slice(indexDeb2, indexFin2 + 1);
        const mergedArray = [...range2, ...range1];

        var j=0;
        for(var i=indexDeb1; i<=indexFin2; i++)
        {
          eventCategories[i]=mergedArray[j];
          j++
        }

        setEventCategories([...eventCategories]);
    })
    .catch((error: any) => {

      if (error.response?.status === 401) {
        alert("Erreur lors de l'échange, vous n'êtes pas le créateur du voyage.")
        navigate('/');
      }
      else
      {
        alert("Erreur lors de l'échange");
      }
    })
  }
  else
  {
    alert("Erreur, lors de l'échange. Veuillez actualiser la page.")
  }
} catch (error) {
  console.error(error);
  alert("Erreur lors de la modification !");
}
};

const processModifVoyage = async () => {

const verrouillageResultat = await verrouillageModif();

if (verrouillageResultat === 1) {
  modifVoyage();
}
};

processModifVoyage();
}; 

const handlePublicClick = () => {
  if(window.confirm(
    t('alertVoyagePublic')
  ))
  {
    voyageService.modifierVoyage({
      id_voyage: voyageKey,
      visibilite: "prive"
  })
      .then((response) => {
      if (response.data.message !== undefined && response.data.message !== null && response.data.message === 'ok') {
          window.location.reload();

      } else {
          // Gérer le cas où idVoyage est 0 ou null
      }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          alert("Erreur lors du changement de visibilité, vous n'êtes plus connecté(e).")
          localStorage.removeItem('xsrfToken');
          navigate('/login');
        }
        else
        {
          alert("Erreur lors du changement de visiblité");
        }

      });

  }
}


const handlePriveClick = () => {
  if(username && username !== '' && username !== 'null')
  {
  if(window.confirm(
    t('alertVoyagePrive')
  ))
  {
    voyageService.modifierVoyage({
      id_voyage: voyageKey,
      visibilite: "public"
  })
      .then((response) => {
      if (response.data.message !== undefined && response.data.message !== null && response.data.message === 'ok') {
          window.location.reload();

      } else {
          // Gérer le cas où idVoyage est 0 ou null
      }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          alert("Erreur lors du changement de visibilité, vous n'êtes plus connecté(e).")
          localStorage.removeItem('xsrfToken');
          navigate('/login');
        }
        else
        {
          alert("Erreur lors du changement de visiblité");
        }

      });

  }
}
else
{
  alert("Pour rendre un voyage public, vous devez remplir un nom d'utilisateur dans votre profil.");
}
}

const handleConvertisseurClick = () => {
  if(etatVoyage !== 0)
  {
    openModalConvertisseur();
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}


const handleStatistiquesClick = async () => {
  if(etatVoyage !== 0)
  {
    try {
      
      const response = await voyageService.getStatistiques(voyageKey, emailPartage, codePartage);

      if(response.data.budget !== 0)
      {
        var budgetCal=Math.round(response.data.budget/nb_voyageurs/nbJours);
        setStatBudget(budgetCal.toString());
      }
      else
      {
        setStatBudget("0");
      }

      setStatNbEtapes(response.data.nbEtapes);
      setStatDistance(response.data.distance);
      setStatDistanceAvion(response.data.distanceAvion);
      setStatDistanceBateau(response.data.distanceBateau);
      setStatDistanceTrain(response.data.distanceTrain);
      setStatDistanceBus(response.data.distanceBus);
      setStatDistanceTaxi(response.data.distanceTaxi);
      setStatDistanceVoiture(response.data.distanceVoiture);
      setStatDistanceMoto(response.data.distanceMoto);
      setStatDistanceVelo(response.data.distanceVelo);
      setStatDistancePied(response.data.distancePied);
    
      
      openModalStatistiques();

    } catch (error: any) {
  
      if (error.response?.status === 401) {
        localStorage.removeItem('xsrfToken');
      }
      console.error("Error fetching data:", error);
      navigate('/');
    }
    
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}

const handleModifResumeClick = async (idPays: string, firstDay: string, firstDayId: number, nuits: EventType[]) => {
  const orderedNights = eventCategories.flatMap((day) =>
    day.evenements
      .filter((event) => event.typeEv === "N")
      .map((event) => ({
        event,
        date: day.date_journee.split(" ").pop() || firstDay,
        dayId: day.id_journee,
      }))
  );
  const selectedNightIds = new Set(nuits.map((nuit) => nuit.id));
  const selectedIndices = orderedNights
    .map(({ event }, index) => (selectedNightIds.has(event.id) ? index : -1))
    .filter((index) => index !== -1);

  if (selectedIndices.length === 0) {
    setFirstDayModif(firstDay);
    setFirstDayIdModif(firstDayId);
    setNuitsModif(nuits);
    setInitialStartIndexModif(0);
    setInitialNbNuitsModif(nuits.length);
  } else {
    const isFreeNight = (event: EventType) => {
      const city = (event.libelle1 || "").trim();
      const cityIsEmpty =
        !city ||
        city === t("cliquezCompleter") ||
        city === "Cliquez pour compléter" ||
        city === "Click to add";

      return (
        event.invisible1 !== "O" &&
        cityIsEmpty
      );
    };
    const selectedStart = Math.min(...selectedIndices);
    const selectedEnd = Math.max(...selectedIndices);
    let availableStart = selectedStart;
    let availableEnd = selectedEnd;

    while (
      availableStart > 0 &&
      isFreeNight(orderedNights[availableStart - 1].event)
    ) {
      availableStart--;
    }

    while (
      availableEnd < orderedNights.length - 1 &&
      isFreeNight(orderedNights[availableEnd + 1].event)
    ) {
      availableEnd++;
    }

    const availableNights = orderedNights.slice(
      availableStart,
      availableEnd + 1
    );

    setFirstDayModif(availableNights[0].date);
    setFirstDayIdModif(availableNights[0].dayId);
    setNuitsModif(availableNights.map(({ event }) => event));
    setInitialStartIndexModif(selectedStart - availableStart);
    setInitialNbNuitsModif(selectedEnd - selectedStart + 1);
  }
  setPaysModif(idPays);

  openModalModifResume();
}

const handleLieuxResumeClick = async (nuits: EventType[]) => {

    try {

      var listeLieuxVisites: string[] = [];
      var listeLieuxVisitesTemp="";

      nuits.forEach((nuit) => {
  eventCategories.forEach((day) => {

    if (day.date_journee && day.date_journee !== "") {
      listeLieuxVisitesTemp = day.date_journee.split(" ")[1] + " :";
    } else {
      listeLieuxVisitesTemp = `Jour ${day.num_jour} :`;
    }

    day.evenements.forEach((evenement) => {
            if(evenement.typeEv === "L" && (evenement.type === null || evenement.type === ""))
            {
              if(evenement.libelle1 !== null && evenement.libelle1 !== "")
              {
                listeLieuxVisitesTemp+=" "+evenement.libelle1 + " - ";
              }
            }
            if(evenement.typeEv === "N")
            {
              if(evenement.id === nuit.id)
              {
                  if((day.date_journee && day.date_journee !== '' && listeLieuxVisitesTemp === `${day.date_journee.split(" ")[1]} :`) || ((!day.date_journee || day.date_journee === '') && listeLieuxVisitesTemp === `Jour ${day.num_jour} :`))
                  {
                    listeLieuxVisites.push(listeLieuxVisitesTemp + " " +t('aucunLieuRenseigne'));
                  }
                  else
                  {
                    listeLieuxVisites.push(listeLieuxVisitesTemp.substring(0, listeLieuxVisitesTemp.length-3));
                  }
                  listeLieuxVisitesTemp="";
              }
              else
              {
                listeLieuxVisitesTemp="";
              }
            }
            
          });
        });
      });

      setLieuxVisites(listeLieuxVisites);

      console.log("listeLieuxVisites :", listeLieuxVisites);
    
      openModalLieuxResume();

    } catch (error: any) {
  
      if (error.response?.status === 401) {
        localStorage.removeItem('xsrfToken');
      }
      console.error("Error fetching data:", error);
      navigate('/');
    }
}

const handleContenuBagageClick = async () => {
  if(etatVoyage !== 0)
  {
      openModalContenuBagage(); 
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}

const handleIntegrationClick = async () => {
  if(etatVoyage !== 0)
  {
      openModalIntegration(); 
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}


const handlePartageClick = async () => {
  if(etatVoyage !== 0)
  {
      openModalPartage(); 
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}

const handleImportClick = async () => {
  if(etatVoyage !== 0)
  {
      openModalImport(); 
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}


const handleExportClick = async () => {
  if(etatVoyage !== 0)
  {
      openModalExport(); 
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}

const handleEditionPDFClick = async () => {
  if(etatVoyage !== 0)
  {
    openModalEditionPDF(); 
  }
  else
  {
    alert("Vous devez être connecté(e) à un compte pour utiliser les outils !");
  }
}

  return (
    <DndProvider backend={HTML5Backend}>
      <CardDragPreview />
  
  { (numDroit === 1 || numDroit === 0) ? (
  <div className={`flex items-center justify-between space-x-5 mt-0 mb-10 ${ visibilite === "public" ? "boutonsOutils" : ""}`}>
<div className="flex-grow"> {/* Cette division prendra tout l'espace disponible */}
    <div className="text-xxs md:text-xs text-center relative">
      <div>
        <button
          onClick={toggleModif}
          className={`w-10 h-6 rounded-full p-1 transition-colors ${
            isModif ? 'bg-rosetrip-500' : 'bg-gray-300'
          }`}
        >
          <span className={`rounded-full block w-4 h-4 bg-white shadow-md transform transition-transform ${isModif ? 'translate-x-full' : ''}`} />
        </button>
      </div>
      <div className="pt-2 dark:text-white">
        {isModif ? (
          <span>{t('modeModif')}</span> 
        ) : (
          <span>{t('modeConsult')}</span> 
        )}
        &nbsp;
      </div>
    </div>
  </div>

  <div className="flex space-x-5">
     { (numDroit === 0 || numDroit === 1) && (
          <AIButton  onClick={handleIAClick}/>
    )}
    <BtnOutils 
      onConvertisseurClick={handleConvertisseurClick}
      onStatistiquesClick={handleStatistiquesClick} 
      onContenuBagageClick={handleContenuBagageClick}
      onImportClick={handleImportClick}
      onExportClick={handleExportClick}
      visibilite={visibilite}
      onIntegrationClick={visibilite && visibilite === 'public' ? handleIntegrationClick : () => {}}
      onPartageClick={visibilite && visibilite === 'public' ? handlePartageClick : () => {}}
      onEditionPDFClick={handleEditionPDFClick}
    />
    { etatVoyage !== 0 && (
      <>
        { visibilite === "public" ? (
          <>
          <ButtonSecondary 
          sizeClass="px-2 py-2 sm:px-2"
          fontSize = "text-sm sm:text-sm font-medium"
          onClick={handlePublicClick}>
            <i className="las la-lock-open text-md"></i>
            <span className="ml-1">Public</span>
          </ButtonSecondary>
          <div className="items-center flex">
            <BtnLikeVoyageIcon nbLikes={nbLikes} isLiked={isLiked} className="absolute z-[1]" onLikeClick={handleLikeClick} />
          </div>
        </>
        ) : (
          <>
          <ButtonSecondary 
          sizeClass="px-2 py-2 sm:px-2"
          fontSize = "text-sm sm:text-sm font-medium"
          onClick={(username && username !== '' && username !== 'null') ? handlePriveClick : () => alert("Pour rendre un voyage public, vous devez remplir un nom d'utilisateur dans votre profil.")}>
            <i className="las la-lock text-md"></i>
            <span className="ml-1">{t('prive')}</span>
          </ButtonSecondary>
          <div className="items-center flex">
          </div>
          </>
        )}
      </>
    )}
  </div>
</div>
)  :  visibilite === "public" ? (
  <div className={`flex items-center justify-between space-x-5 mt-0 mb-10 ${ visibilite === "public" ? "boutonsOutils" : ""}`}>
  <div className="flex-grow"> 
  </div>
    <div className="flex space-x-5 ">
            <ButtonSecondary onClick={handlePartageClick} 
            sizeClass="px-2 py-2 sm:px-2"
            fontSize = "text-sm sm:text-sm font-medium">
              <i className="las la-share-alt text-md"></i>
              <span className="ml-1">{t('partage')}</span>
            </ButtonSecondary>
            <div className="items-center flex">
              <BtnLikeVoyageIcon nbLikes={nbLikes} isLiked={isLiked} className="absolute  z-[1]" onLikeClick={handleLikeClick} />
            </div>
    </div>
  </div>
) : (null)}
{ numDroit === 1 && mailKo && (
  <div className="sticky texteGras items-center text-center space-x-5 mb-6 text-xs md:text-sm lg:text-sm xl:text-sm ">
    <p><i className='las la la-warning'></i> L'email utilisé pour créer votre <a href='/account'><u>compte</u></a> est erroné, veuillez <a href='/contact'><u>nous contacter</u></a> pour le rectifier !</p>
  </div>
)}
    { !isModif && groupedNights.length === 0 
    ? (<div className="mt-4 mb-20 text-sm text-center sticky">Aucune ville localisée n'est présente pour les éléments nuits</div>)
    :
    (<ItemsSlider title="" nomClass="item-slider-container">
    {groupedNights.map((group: any, index: number) => (
        <div key={index} className="text-center">
         <div className="relative flex items-center justify-center mb-10 w-full">

        {/* Ligne gauche */}
        <div className="flex-1 border-t border-neutral-400"></div>

        {/* Centre */}
        <div className="relative flex items-center justify-center px-10">

          <h2
            className={`
              text-md font-bold tracking-tight text-center whitespace-nowrap
              ${
                group.nom_pays === t('nuitsIncompletes')
                  ? "couleurRose"
                  : "text-neutral-900 dark:text-white"
              }
            `}
          >
            {group.nom_pays}
          </h2>

          {/* Drapeau */}
          {flags[group.pays] &&
          flags[group.pays] !== "loading" && (
            <span className="ml-3 text-2xl leading-none">
              <img
                src={flags[group.pays] || ""}
                alt={group.nom_pays}
                className="h-4 w-6 rounded-sm object-cover"
              />
            </span>
          )}

          {/* Flèche échange */}
          {isModif && index < groupedNights.length - 1 && (
            <div
              data-tooltip-id="myTooltip"
              data-tooltip-content="Cliquez pour échanger l'ordre de visite des pays"
              data-tooltip-place="top"
              className="absolute -bottom-9 right-0"
            >
              <i
                onClick={() =>
                  echangerNuits(
                    group.groupVilles[0].firstDayId,
                    groupedNights[index + 1].groupVilles[0].firstDayId,
                    index + 1
                  )
                }
                className="iconeTransport las la-exchange-alt text-lg cursor-pointer"
              ></i>
            </div>
          )}
        </div>

        {/* Ligne droite */}
        <div className="flex-1 border-t border-neutral-400"></div>

        <Tooltip id="myTooltip" />
      </div>
         
          {group.groupVilles.map((ville: any, villeIndex: number) => {
             const isFirst =
            index === 0 &&
            villeIndex === 0;

          const isLast =
            index === groupedNights.length - 1 &&
            villeIndex === group.groupVilles.length - 1;
            const cardContent = (
              <CardNuitResume
                key={villeIndex}
                libelle={ville.libelle1}
                jour={ville.firstDay}
                nbNuits={ville.nights.length}
                image={ville.image}
                images={ville.images}
                imageItems={ville.imageItems}
                pays={group.pays}
                isModif={isModif}
                isFirst={isFirst}
                isLast={isLast}
                typeTransport={ville.typeTransport}
                onLieuxResumeClick={!isModif ? () => handleLieuxResumeClick(ville.nights) : () => handleModifResumeClick(group.pays, ville.firstDay, ville.firstDayId, ville.nights)}
              />
            );

  return isModif ? (
  <DraggableCard
    key={`${index}-${villeIndex}`}
    index={index}
    id_journee={ville.firstDayId}
    eventCategories={eventCategories}
    groupedNights={groupedNights}
    previewData={{
      libelle: ville.libelle1,
      jour: ville.firstDay,
      image: ville.image || "",
      nbNuits: ville.nights.length,
    }}
    onDrop={(dragIndex, hoverIndex, draggedIdJournee) =>
      moveCard(
        dragIndex,
        hoverIndex,
        draggedIdJournee,
        ville.firstDayId,
        index
      )
    }
  >
    {cardContent}
  </DraggableCard>
) : numDroit === 1 ? (
  <div
    key={`${index}-${villeIndex}-readonly`}
    draggable
    onDragStart={handleDragAttemptWithoutModif}
  >
    {cardContent}
  </div>
) : (
  cardContent
);
          })}
        </div>
      ))}
  </ItemsSlider>
  )}
  
<ModalPlannerAI
  idVoyage={voyageKey}
  nbJours={nbJours}
  jours={eventCategories.map((jour) => ({
    num_jour: jour.num_jour,
    date_journee: jour.date_journee,
  }))}
  isOpen={isAI}
  onClose={() => setIsAI(false)}
  onApplyActions={handleApplyAIActions}
/>


{isModalConvertisseurOpen && (
       <ConvertisseurMonnaie onConvertisseurCloseModal={closeModalConvertisseur} />
       )}

{isModalBookingOpen && (
       <ModalBooking
       onBookingCloseModal={closeModalBooking} 
       ville={lieu1}
       latitude={latitude1}
       longitude={longitude1}
       hebergement={lieu2}
       latitudeHeb={latitude2}
       longitudeHeb={longitude2}
       />
       )}

{isModalStatistiquesOpen && (
       <ModalStatistiques 
        onStatistiquesCloseModal={closeModalStatistiques} 
        nbEtapes={statNbEtapes}
        budget={statBudget}
        nbJours={nbJours}
        monnaie={symbole_monnaie}
        distance={statDistance}
        distanceAvion={statDistanceAvion}
        distanceBateau={statDistanceBateau}
        distanceTrain={statDistanceTrain}
        distanceBus={statDistanceBus}
        distanceTaxi={statDistanceTaxi}
        distanceVoiture={statDistanceVoiture}
        distanceMoto={statDistanceMoto}
        distanceVelo={statDistanceVelo}
        distancePied={statDistancePied}
        />
       )}

{isModalLieuxResumeOpen && (
       <ModalLieuxResume 
        onLieuxResumeCloseModal={closeModalLieuxResume} 
        lieux={lieuxVisites}
        />
       )}

{isModalModifResumeOpen && (
       <ModalModifResume 
        onModifResumeCloseModal={closeModalModifResume} 
        nuits={nuitsModif}
        date={firstDayModif}
        idDate={firstDayIdModif}
        listeNomPays={libelles_pays}
        listeIdPays={id_pays}
        pays={paysModif}
        voyageKey={voyageKey}
        onDisplayElementChange={handleElementChange}
        eventCategories={eventCategories}
        initialStartIndex={initialStartIndexModif}
        initialNbNuits={initialNbNuitsModif}
        />
       )}

{isModalContenuBagageOpen && (
       <ModalContenuBagage 
       onContenuBagageCloseModal={closeModalContenuBagage} 
       idVoyage={voyageKey}
        />
       )}

{isModalIntegrationOpen && (
       <ModalIntegration 
       onIntegrationCloseModal={closeModalIntegration} 
       idVoyage={voyageKey}
       affichageBudget={affichageBudget}
        />
    )}

{isModalPartageOpen && (
       <ModalPartageRS  onClose={closeModalPartage}
       keyVoyage={Number(voyageKey)}
       onIntegrationClick={visibilite && visibilite === 'public' ? handleIntegrationClick : () => {}}
        />
    )}

{isModalImportOpen && (
       <ModalImport 
       onImportCloseModal={closeModalImport} 
       idVoyage={voyageKey}
       nbJours={nbJours}
       eventCategories={eventCategories}
       onAddEvenement={handleAddEvenement as OnAddEvenementType}
        />
    )}

    { isModalEditionPDFOpen && (
            <ModalEditionPDF isCreateur={true} isCoCreateur={true} onClose={closeModalEditionPDF} keyVoyage={Number(voyageKey)}
            partageConfirmations='O'
            partagePrix='O'
            partageBlocNotes='O'
            />
          )}


{isModalExportOpen && (
       <ModalExport 
       onExportCloseModal={closeModalExport} 
       idVoyage={voyageKey}
        />
       )}

       
{isAdding && (
  <Transition appear show={true} as={Fragment}>
    <Dialog
      as="div"
      className="fixed inset-0 z-[100] overflow-y-auto" // Plus élevé pour l'attente
      onClose={() => {}} // Pas de fermeture pour la modale d'attente
    >
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-60" />
        </Transition.Child>
        
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            className="inline-block transition-all transform rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 dark:text-neutral-100 shadow-xl px-8 py-4"
          >
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-neutral-200">
              {t('ajoutEnCours')}
            </h3>
          </div>
          
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
)}

  </DndProvider>
  );
};

export default PlanificateurResume;
